// deps
import React, { ReactElement, memo, useMemo } from "react";

// interfaces
import { IPreferredPaymentGroup } from "@payment-ui/interfaces";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getPaymentGroupDisplayName, getPaymentGroupDisableMessage } from "@payment-ui/helpers/Payment";
import { format } from "@swiggy-private/core-ui";

// components
import PaymentGroupContainer from "@payment-ui/components/payment-group/components/Container";
import PaymentGroupTitle from "@payment-ui/components/payment-group/components/Title";
import PreferredMethodRenderer from "./components/preferred-method-renderer";
import ShowMoreCta from "../components/ShowMoreCta/ShowMoreCta";

// test-ids
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";

// hooks
import { usePreferredPaymentGroup } from "./hooks";

import styles from "../PaymentGroup.v4.scss";

// constants
import { TEXTS } from "./constants";

function PreferredPaymentGroup(props: IPreferredPaymentGroup): ReactElement | null {
    const {
        paymentAmount,
        user,
        onPreferredMoreCtaClick,
        credEligibleData,
        juspayMeta,
        hideGroupTitle,
        paymentData,
        paymentGroupBanner,
    } = props;

    const { getPaymentActions, getPaymentMethodProps, paymentMethods, canShowPreferredMoreCta, foldedMethodsCount } =
        usePreferredPaymentGroup(props);

    const getMethodCount = useMemo(() => {
        return format(TEXTS.METHOD_COUNT, String(foldedMethodsCount));
    }, [foldedMethodsCount]);

    if (isEmpty(paymentMethods)) {
        return null;
    }

    return (
        <PaymentGroupContainer>
            {!hideGroupTitle && (
                <PaymentGroupTitle
                    title={getPaymentGroupDisplayName(paymentData)}
                    disableMessage={getPaymentGroupDisableMessage(paymentData)}
                    data-testid={PAYMENT_GROUP_TEST_IDS.TITLE.PREFERRED}
                />
            )}
            <div className={styles.paymentGroupContainer}>
                {paymentMethods.map((paymentMethod) => (
                    <PreferredMethodRenderer
                        key={paymentMethod.displayName}
                        methodData={paymentMethod}
                        getPaymentActions={getPaymentActions}
                        getPaymentMethodProps={getPaymentMethodProps}
                        paymentAmount={paymentAmount}
                        user={user}
                        credEligibleData={credEligibleData}
                        juspayMeta={juspayMeta}
                        paymentGroupBanner={paymentGroupBanner}
                    />
                ))}
                {canShowPreferredMoreCta && (
                    <ShowMoreCta
                        handleClick={onPreferredMoreCtaClick}
                        dataTestId={PAYMENT_GROUP_TEST_IDS.PREFERRED.MORE_METHODS_CTA}
                        methodCount={getMethodCount}
                        text={TEXTS.VIEW_ALL_PREFERRED_METHODS}
                    />
                )}
            </div>
        </PaymentGroupContainer>
    );
}

export default memo(PreferredPaymentGroup);
